import {
	Input,
	PaginatedDataTable,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Tabs,
	TabsList,
	TabsTriggerWithNumber,
	useSidebar,
} from "@dune/ui";
import { parseAsInteger, useQueryState } from "nuqs";
import { useNavigate } from "react-router-dom";
import { usePaginationStore } from "../../stores/pagination.store";
import {
	useReceiptCountsQuery,
	useReceiptsQuery,
} from "../../api/queries/receipt.queries";
import PageLayoutWithTabs from "../../component/layout/PageLayoutWithTabs";
import { receiptColumns } from "./data-table/receipt-columns";
import { PAGES } from "@dune/types";

const ReceiptListPage = () => {
	const navigate = useNavigate();

	const [status, setStatus] = useQueryState("status", {
		defaultValue: "all",
	});
	const [project, setProject] = useQueryState("project");
	const [type, setType] = useQueryState("type");
	const [query, setQuery] = useQueryState("query", {
		defaultValue: "",
	});
	const storedLimit = usePaginationStore((s) => s.limit);
	const setStoredLimit = usePaginationStore((s) => s.setLimit);
	const [page, setPage] = useQueryState("page", parseAsInteger.withDefault(1));
	const [limitParam, setLimitParam] = useQueryState(
		"limit",
		parseAsInteger.withDefault(storedLimit),
	);
	const limit = limitParam ?? storedLimit;

	const { state } = useSidebar();
	const isCollapsed = state === "collapsed";

	// Use the receipts query with filters
	const { data: receiptsResponse, isLoading } = useReceiptsQuery({
		status,
		type,
		query,
		project,
		page,
		limit,
	});

	const receipts = receiptsResponse?.data || [];

	const paginationMeta = receiptsResponse?.meta || {
		page,
		limit,
		total: 0,
		totalPages: 1,
		hasNextPage: false,
		hasPreviousPage: false,
	};

	const handlePageChange = (newPage: number) => {
		setPage(newPage);
	};

	const handleLimitChange = (newLimit: number) => {
		setLimitParam(newLimit);
		setStoredLimit(newLimit);
		setPage(1);
	};

	const handleStatusChange = (value: string) => {
		setStatus(value);
		setPage(1);
	};

	// Get counts for all receipts (not filtered)
	const { data: counts } = useReceiptCountsQuery();

	const statusCounts = {
		all: counts?.all ?? 0,
		pending: counts?.pending ?? 0,
		paid: counts?.paid ?? 0,
		refund: counts?.refunded ?? 0,
		cancelled: counts?.cancelled ?? 0,
	};

	return (
		<PageLayoutWithTabs title="Quittances" tabContent={{}}>
			<div className="flex flex-col space-y-6 flex-1 min-h-0">
				<Tabs value={status} onValueChange={handleStatusChange}>
					<TabsList>
						<TabsTriggerWithNumber
							value={"all"}
							variant="compact"
							number={statusCounts.all}
						>
							Tous
						</TabsTriggerWithNumber>
						<TabsTriggerWithNumber
							value={"pending"}
							variant="compact"
							number={statusCounts.pending}
						>
							À régler
						</TabsTriggerWithNumber>
						<TabsTriggerWithNumber
							value={"paid"}
							variant="compact"
							number={statusCounts.paid}
						>
							Réglées
						</TabsTriggerWithNumber>
						<TabsTriggerWithNumber
							value={"refund"}
							variant="compact"
							number={statusCounts.refund}
						>
							À rembourser
						</TabsTriggerWithNumber>
						<TabsTriggerWithNumber
							value={"cancelled"}
							variant="compact"
							number={statusCounts.cancelled}
						>
							Annulées
						</TabsTriggerWithNumber>
					</TabsList>
				</Tabs>
				<div className="flex flex-row justify-between">
					<div className="flex flex-row space-x-4">
						<Select
							value={project ?? undefined}
							onValueChange={(value) => {
								setProject(value);
								setPage(1);
							}}
						>
							<SelectTrigger>
								<SelectValue placeholder="Projets" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">Tous</SelectItem>
								<SelectItem value="DO">DO</SelectItem>
								<SelectItem value="MRT">MRT</SelectItem>
								<SelectItem value="TRC">TRC</SelectItem>
							</SelectContent>
						</Select>
						<Select
							value={type ?? undefined}
							onValueChange={(value) => {
								setType(value);
								setPage(1);
							}}
						>
							<SelectTrigger>
								<SelectValue placeholder="Type" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">Tous</SelectItem>
								<SelectItem value="AFN">AFN</SelectItem>
								<SelectItem value="AVT">AVT</SelectItem>
								<SelectItem value="ANNUL">ANNUL</SelectItem>
							</SelectContent>
						</Select>
					</div>
					<div>
						<Input
							placeholder="Rechercher rapide"
							value={query}
							onChange={(event) => {
								setQuery(event.target.value);
								setPage(1);
							}}
							className="w-80"
						/>
					</div>
				</div>
				<div className="flex-1 min-h-0 w-full overflow-auto">
					<div className={`${isCollapsed ? "max-w-[92vw]" : "max-w-[80vw]"}`}>
						<PaginatedDataTable
							data={receipts}
							columns={receiptColumns}
							meta={paginationMeta}
							onPageChange={handlePageChange}
							onLimitChange={handleLimitChange}
							isLoading={isLoading}
							noResultsText="Aucune quittance trouvée"
							onRowClick={(row) =>
								navigate(`${PAGES.RECEIPTS.show.withParams(row.id.toString())}`)
							}
						/>
					</div>
				</div>
			</div>
		</PageLayoutWithTabs>
	);
};

export default ReceiptListPage;
